import { useCallback, useEffect, useRef, useState } from 'react';
import { OperationVariables, useApolloClient } from '@apollo/client';
import { toast } from 'react-hot-toast';
import {
  GetCommunityPinnedPostsDocument,
  GetCommunityPostForEditingDocument,
  GetCommunityPostForEditingQuery,
  GetCommunityPostForEditingQueryVariables,
  GetCommunityPostsDocument,
  GetCommunityPostsQuery,
  GetCommunityPostsQueryVariables,
  useGetCommunityPostForEditingLazyQuery,
} from 'generated/graphql';
import { useAsyncQueryRaw } from '@client/new/helpers/gql';
import { Async, toAsyncError, toAsyncFetched } from '@client/new/helpers/types';
import { CommunityId } from '../model/community.types';
import { PostMediaItem, toPostId } from '../model/post.types';
import { toMediaItem } from '../model/post.mappers';

type CommunityPostEditorValue = {
  id: ReturnType<typeof toPostId>;
  text: string;
  media: PostMediaItem[];
  published: boolean;
};

type CommunityPostEditor = {
  state: Async<CommunityPostEditorValue>;
  open: (postId: string) => Promise<void>;
  close: () => void;
  saved: (changed: boolean) => void;
};

const toEditorValue = (
  data: GetCommunityPostForEditingQuery,
  postId: string,
): CommunityPostEditorValue => {
  const post = data.post;
  if (!post || post.id !== postId) throw new Error('Post unavailable');
  if (
    post.media.some(
      (item) =>
        (item.status === 'processing' || item.status === 'failed') &&
        !item.uuid,
    )
  ) {
    throw new Error('Post contains media without an identifier');
  }
  const media = post.media.reduce<PostMediaItem[]>((items, item) => {
    const mapped = toMediaItem(item);
    if (!mapped) throw new Error('Post contains unsupported media');
    items.push(mapped);
    return items;
  }, []);
  return {
    id: toPostId(post.id),
    text: post.text,
    media,
    published: post.status !== 'DRAFT' && post.status !== 'SCHEDULED',
  };
};

const matchesCommunity = (
  variables: OperationVariables | undefined,
  communityId: CommunityId,
): variables is GetCommunityPostsQueryVariables =>
  typeof variables === 'object' &&
  variables !== null &&
  'id' in variables &&
  variables.id === communityId;

const isFiltered = (filters: GetCommunityPostsQueryVariables['filters']) =>
  Boolean(filters && Object.keys(filters).length > 0);

export const useCommunityPostEditor = (
  communityId: CommunityId,
): CommunityPostEditor => {
  const client = useApolloClient();
  const generationRef = useRef(0);
  const lastMappedErrorGenerationRef = useRef<number | null>(null);
  const [activePostId, setActivePostId] = useState<string | null>(null);
  const [execute, query] = useGetCommunityPostForEditingLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const { data: mapped } = useAsyncQueryRaw({
    query,
    mapper: (data) => {
      const postId = query.variables?.postId;
      if (!postId || query.variables?.communityId !== communityId)
        return {
          generation: generationRef.current,
          source: data,
          result: toAsyncError('Could not load this post for editing'),
        };
      try {
        return {
          generation: generationRef.current,
          source: data,
          result: toAsyncFetched(toEditorValue(data, postId)),
        };
      } catch {
        return {
          generation: generationRef.current,
          source: data,
          result: toAsyncError('Could not load this post for editing'),
        };
      }
    },
  });

  const open = useCallback(
    async (postId: string) => {
      const generation = ++generationRef.current;
      lastMappedErrorGenerationRef.current = null;
      setActivePostId(postId);
      try {
        await execute({
          variables: { communityId, postId },
          fetchPolicy: 'no-cache',
        });
      } catch {
        if (generationRef.current !== generation) return;
        lastMappedErrorGenerationRef.current = generation;
        toast.error('Could not load this post for editing');
      }
    },
    [communityId, execute],
  );

  const close = useCallback(() => {
    generationRef.current += 1;
    setActivePostId(null);
  }, []);

  const saved = useCallback(
    (changed: boolean) => {
      const postId = activePostId;
      close();
      if (!changed || !postId) return;
      void refreshCommunityPost(client, communityId, postId);
    },
    [activePostId, client, close, communityId],
  );

  let state: Async<CommunityPostEditorValue> = { type: 'not-fetched' };
  if (activePostId) {
    if (
      query.error &&
      query.variables?.postId === activePostId &&
      query.variables?.communityId === communityId
    ) {
      state = toAsyncError('Could not load this post for editing');
    } else if (
      query.variables?.postId !== activePostId ||
      query.variables?.communityId !== communityId ||
      query.loading
    ) {
      state = { type: 'fetching' };
    } else if (
      mapped.type === 'fetched' &&
      mapped.value.generation === generationRef.current &&
      mapped.value.source === query.data
    ) {
      state = mapped.value.result;
    } else if (mapped.type === 'fetched' || mapped.type === 'not-fetched') {
      state = { type: 'fetching' };
    } else {
      state = mapped;
    }
  }

  useEffect(() => {
    if (
      activePostId &&
      state.type === 'fetch-error' &&
      lastMappedErrorGenerationRef.current !== generationRef.current
    ) {
      lastMappedErrorGenerationRef.current = generationRef.current;
      toast.error('Could not load this post for editing');
    }
  }, [activePostId, query.error, state.type]);

  return { state, open, close, saved };
};

const refreshCommunityPost = async (
  client: ReturnType<typeof useApolloClient>,
  communityId: CommunityId,
  postId: string,
): Promise<void> => {
  const variables: GetCommunityPostForEditingQueryVariables = {
    communityId,
    postId,
  };
  let refreshFailed = false;
  try {
    await client.query({
      query: GetCommunityPostForEditingDocument,
      variables,
      fetchPolicy: 'network-only',
    });
  } catch {
    refreshFailed = true;
  }

  const refreshes = [...client.getObservableQueries('all')].flatMap(
    ([, observable]) => {
      if (!observable.hasObservers()) return [];
      if (observable.options.fetchPolicy === 'standby') return [];
      const query = observable.options.query;
      if (query === GetCommunityPinnedPostsDocument) {
        const queryVariables = observable.variables;
        return queryVariables?.id === communityId &&
          observable.options.fetchPolicy !== 'cache-only'
          ? [Promise.resolve().then(() => observable.refetch())]
          : [];
      }
      if (query !== GetCommunityPostsDocument) return [];

      const queryVariables = observable.variables;
      if (!matchesCommunity(queryVariables, communityId)) return [];
      const isOlderPage = Boolean(
        queryVariables.direction || queryVariables.lastId,
      );
      if (!isOlderPage) {
        return observable.options.fetchPolicy !== 'cache-only'
          ? [Promise.resolve().then(() => observable.refetch())]
          : [];
      }

      if (
        observable.options.fetchPolicy !== 'cache-only' ||
        !isFiltered(queryVariables.filters)
      ) {
        return [];
      }
      const cached = client.cache.diff<GetCommunityPostsQuery>({
        query: GetCommunityPostsDocument,
        variables: queryVariables,
        optimistic: false,
        returnPartialData: true,
      });
      const containsEditedPost = cached.result?.communityPosts?.posts.some(
        (post) => post.id === postId,
      );
      return containsEditedPost
        ? [Promise.resolve().then(() => observable.refetch())]
        : [];
    },
  );
  const refreshResults = await Promise.allSettled(refreshes);
  if (refreshResults.some((result) => result.status === 'rejected')) {
    refreshFailed = true;
  }
  if (refreshFailed)
    toast.error('Post saved, but some views could not refresh');
};
